import { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

type Props = {
    url: string;
    onLoadError?: (message: string) => void;
};

export function ModelViewer({ url, onLoadError }: Props) {
    const hostRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x0b0616);

        const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 2000);
        camera.position.set(0.8, 0.6, 1.2);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        host.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;

        // luci
        scene.add(new THREE.HemisphereLight(0xffffff, 0x220022, 1));
        const dir = new THREE.DirectionalLight(0xffffff, 1);
        dir.position.set(2, 3, 2);
        scene.add(dir);

        const resize = () => {
            const w = host.clientWidth;
            const h = host.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };
        resize();

        const ro = new ResizeObserver(resize);
        ro.observe(host);

        // -------- LOADER AUTO --------

        const isBlobUrl = url.startsWith("blob:");
        const ext = isBlobUrl ? "glb" : url.split(".").pop()?.toLowerCase();

        if (ext === "glb" || ext === "gltf") {
            const loader = new GLTFLoader();

            loader.load(
                url,
                (gltf) => {
                    const model = gltf.scene;
                    normalizeAndAdd(model, scene);
                },
                undefined,
                () => {
                    onLoadError?.("Modello scaricato ma non leggibile come GLB. Verifica il contenuto del file o CORS.");
                }
            );
        }

        if (ext === "ply") {
            const loader = new PLYLoader();

            loader.load(
                url,
                (geometry) => {
                    geometry.computeVertexNormals();

                    const material = new THREE.MeshStandardMaterial({
                        color: 0xffffff,
                        roughness: 0.25,
                        metalness: 0.1,
                    });

                    const mesh = new THREE.Mesh(geometry, material);
                    normalizeAndAdd(mesh, scene);
                },
                undefined,
                () => {
                    onLoadError?.("Modello scaricato ma non leggibile come PLY.");
                }
            );
        }

        // -------- NORMALIZE --------

        function normalizeAndAdd(obj: THREE.Object3D, scene: THREE.Scene) {
            const box = new THREE.Box3().setFromObject(obj);
            const size = new THREE.Vector3();
            const center = new THREE.Vector3();

            box.getSize(size);
            box.getCenter(center);

            obj.position.sub(center);

            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 1 / maxDim;

            obj.scale.setScalar(scale);

            scene.add(obj);
        }

        // render loop
        let raf = 0;
        const animate = () => {
            raf = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            renderer.dispose();
            host.removeChild(renderer.domElement);
        };
    }, [url]);

    return <div ref={hostRef} style={{ width: "100%", height: "100%" }} />;
}
